/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  DurationUtils,
  Constants,
  Milliseconds,
  Specs,
  translate,
  TimeUtils,
  Asset,
  Time,
  TvAssetUtils,
  RelativeTime,
  ContentTagsUtils,
  isEntitlement,
  DisabledFeaturesValues,
  Entitlement,
  SubscriptionEntitlement,
  CollectionEntitlement,
  FolderType,
  Icons,
  CurrencyCode,
  Badge,
  BadgeType,
  TvAsset,
  Recording,
  SeriesRecording,
  Ducks,
  AssetUtils,
} from '@wit/vtv-x-foundation-lib';
import store from 'store/store';
import { Selectors as PlayableChannelsSelectors } from '../../../store/ducks/epg/playableChannels';
import {
  Selectors as VodProductIdCacheSelectors,
  State as VodProductIdState,
} from '../../../store/ducks/vodProductIdCache';
import { Selectors as EntitlementsAssetsSelectors } from '../../../store/ducks/entitlements/assets/entitledAssets';
import { Selectors as EntitlementsPpvSelectors } from '../../../store/ducks/entitlements/ppv';
import { Selectors as EntitlementsSubscriptionSelectors } from '../../../store/ducks/entitlements/subscription';
import { Selectors as EntitlementsCollectionSelectors } from '../../../store/ducks/entitlements/collection';
import { Selectors as SingleRecordingsSelectors } from '../../../store/ducks/recordings/singleRecordings';
import { Selectors as SeriesRecordingsSelectors } from '../../../store/ducks/recordings/seriesRecordings';
import Colors from '../../../../ui/common/Colors';

export default class BadgeEngine {
  protected static RENTED_ENTITLEMENT_DESTRUCTURED_DURATION = DurationUtils.toDestructuredDuration(
    Constants.RENTED_ENTITLEMENT_DURATION
  );

  private static getExpirationTimeLabel(expirationTime: Milliseconds): string {
    if (expirationTime < Specs.EXPIRATION_BADGE_ONE_HOUR_IN_MILLISCONDS) {
      return translate
        .formatString(translate['badge.expirationTime.minutes'], TimeUtils.millisecondsToMinutes(expirationTime))
        .toString();
    }

    if (
      expirationTime >= Specs.EXPIRATION_BADGE_ONE_HOUR_IN_MILLISCONDS &&
      expirationTime < Specs.EXPIRATION_BADGE_TWO_DAYS_IN_MILLISCONDS
    ) {
      const hours = TimeUtils.millisecondsToHours(expirationTime);
      const minutes = TimeUtils.millisecondsToMinutes(expirationTime - TimeUtils.hoursToMilliseconds(hours));

      if (minutes === 0) {
        return translate.formatString(translate['badge.expirationTime.hours'], hours).toString();
      }

      return `${translate.formatString(translate['badge.expirationTime.hours'], hours).toString()} ${translate
        .formatString(translate['badge.expirationTime.minutes'], minutes)
        .toString()}`;
    }

    return translate
      .formatString(translate['badge.expirationTime.days'], TimeUtils.millisecondsToDays(expirationTime))
      .toString();
  }

  protected static getExpiringInLabel(asset: Asset, translateStr: string): string | undefined {
    const dmsConfigs = Ducks.Dms.Selectors.getDmsConfigs(store().getState());
    if (dmsConfigs) {
      const { expirationBadgeTimeframe, epgBackwardDays } = dmsConfigs;
      const now = TimeUtils.now();

      if (AssetUtils.isRecording(asset) && asset.deleteTime) {
        if (
          TimeUtils.isAfter(now, TimeUtils.subtractDestructuredDuration(asset.deleteTime, expirationBadgeTimeframe)) &&
          TimeUtils.isAfter(asset.deleteTime, now) &&
          TimeUtils.isBefore(asset.actualEndTime, now)
        ) {
          return translate
            .formatString(translateStr, BadgeEngine.getExpirationTimeLabel(TimeUtils.diff(now, asset.deleteTime)))
            .toString();
        }
      } else if (AssetUtils.isEpg(asset)) {
        const programValidation = TimeUtils.addDays(asset.endDate, epgBackwardDays);
        let validationCatchUpEndDate: Time | undefined;
        let validationEpgEndDate: Time | undefined;
        let hasValidExpiredDate = false;

        if (
          TvAssetUtils.getRelativeTime(asset) === RelativeTime.PAST &&
          ContentTagsUtils.isPlaybackAllowedForCatchupAsset(asset)
        ) {
          validationCatchUpEndDate = TimeUtils.addDuration(asset.endDate, Constants.CATCHUP_EXPIRATION_BADGE_TIMEFRAME);

          if (
            validationCatchUpEndDate &&
            TimeUtils.isAfter(validationCatchUpEndDate, now) &&
            TimeUtils.isAfter(programValidation, now)
          ) {
            hasValidExpiredDate = true;
          }
        } else {
          validationEpgEndDate = TimeUtils.subtractDuration(asset.endDate, Constants.EPG_EXPIRATION_BADGE_TIMEFRAME);

          if (validationEpgEndDate && TimeUtils.isAfter(now, validationEpgEndDate)) {
            hasValidExpiredDate = true;
          }
        }

        if (hasValidExpiredDate) {
          return translate
            .formatString(translateStr, BadgeEngine.getExpirationTimeLabel(TimeUtils.diff(now, programValidation)))
            .toString();
        }
      } else if (AssetUtils.isFolder(asset) || AssetUtils.isVod(asset)) {
        const entitlement = BadgeEngine.getAssetEntitled(asset);
        const timeframe = AssetUtils.isFolder(asset)
          ? Constants.FOLDER_EXPIRATION_BADGE_TIMEFRAME
          : Constants.VOD_EXPIRATION_BADGE_TIMEFRAME;

        if (
          entitlement &&
          isEntitlement(entitlement) &&
          TimeUtils.isAfter(now, TimeUtils.subtractDuration(entitlement.endDate, timeframe))
        ) {
          return translate
            .formatString(translateStr, BadgeEngine.getExpirationTimeLabel(TimeUtils.diff(now, entitlement.endDate)))
            .toString();
        }
      }
    }

    return undefined;
  }

  protected static getRemainingTimeLabel(remainingTime: Milliseconds): string {
    if (remainingTime < Specs.BADGES_REMAINING_TIME_MINUTES_IN_MILLISCONDS_LIMIT) {
      return translate
        .formatString(translate['badge.remainingTime.minutes'], TimeUtils.millisecondsToMinutes(remainingTime))
        .toString();
    }

    const hours = TimeUtils.millisecondsToHours(remainingTime);
    const minutes = TimeUtils.millisecondsToMinutes(remainingTime - TimeUtils.hoursToMilliseconds(hours));

    if (minutes === 0) {
      return translate.formatString(translate['badge.remainingTime.hours'], hours).toString();
    }

    return `${translate.formatString(translate['badge.remainingTime.hours'], hours).toString()} ${translate
      .formatString(translate['badge.remainingTime.minutes'], minutes)
      .toString()}`;
  }

  private static isFeatureDisabledByDMS(feature: DisabledFeaturesValues): boolean {
    const storeState = store().getState();
    return Ducks.Dms.Selectors.isFeatureDisabled(storeState, feature);
  }

  static isPurchasedBadgeEnabled(): boolean {
    return !BadgeEngine.isFeatureDisabledByDMS(DisabledFeaturesValues.NO_PURCHASE_BADGE);
  }

  static isPurchasedBadgeEnabledForBoxset(): boolean {
    if (BadgeEngine.isPurchasedBadgeEnabled()) {
      return !BadgeEngine.isFeatureDisabledByDMS(DisabledFeaturesValues.NO_PURCHASE_BADGE_BOXSET);
    }

    return false;
  }

  static isPurchasedBadgeEnabledForSVOD(): boolean {
    if (BadgeEngine.isPurchasedBadgeEnabled()) {
      return !BadgeEngine.isFeatureDisabledByDMS(DisabledFeaturesValues.NO_PURCHASE_BADGE_SVOD);
    }

    return false;
  }

  static isPurchasedBadgeEnabledForTVOD(): boolean {
    if (BadgeEngine.isPurchasedBadgeEnabled()) {
      return !BadgeEngine.isFeatureDisabledByDMS(DisabledFeaturesValues.NO_PURCHASE_BADGE_TVOD);
    }

    return false;
  }

  protected static isEntitlementRented(entitlement: Entitlement): boolean {
    if (BadgeEngine.RENTED_ENTITLEMENT_DESTRUCTURED_DURATION) {
      return TimeUtils.isBefore(
        entitlement.endDate,
        TimeUtils.addDestructuredDuration(TimeUtils.now(), BadgeEngine.RENTED_ENTITLEMENT_DESTRUCTURED_DURATION)
      );
    }

    return false;
  }

  protected static getEntitlementCollectionByMediaFiles(mediaFileIds: number[]): CollectionEntitlement | undefined {
    const vodProductIdFromCache: VodProductIdState[] | undefined =
      VodProductIdCacheSelectors.getVodProductIdByMediaFileIds(store().getState(), mediaFileIds);
    let entitlement: CollectionEntitlement | undefined;

    const entitlementsCollection = EntitlementsCollectionSelectors.getCollectionEntitlements(store().getState());
    vodProductIdFromCache.find((vodProductId) => {
      if (vodProductId.collection?.expires && TimeUtils.isAfterNow(vodProductId.collection.expires)) {
        entitlement = entitlementsCollection.find((e) =>
          vodProductId.collection?.productsIds?.find(
            (productId) => productId === e.id && TimeUtils.isAfterNow(e.endDate)
          )
        );
      }
      return entitlement;
    });
    return entitlement;
  }

  protected static getEntitlementSubscriptionByMediaFiles(mediaFileIds: number[]): SubscriptionEntitlement | undefined {
    const vodProductIdFromCache: VodProductIdState[] | undefined =
      VodProductIdCacheSelectors.getVodProductIdByMediaFileIds(store().getState(), mediaFileIds);
    let entitlement: SubscriptionEntitlement | undefined;

    const entitlementsSubscription = EntitlementsSubscriptionSelectors.getSubscriptionEntitlements(store().getState());
    vodProductIdFromCache.find((vodProductId) => {
      if (vodProductId.subscription?.expires && TimeUtils.isAfterNow(vodProductId.subscription.expires)) {
        entitlement = entitlementsSubscription.find((e) =>
          vodProductId.subscription?.productsIds?.find(
            (productId) => productId === e.id && TimeUtils.isAfterNow(e.endDate)
          )
        );
      }
      return entitlement;
    });
    return entitlement;
  }

  protected static getAssetEntitledForVod(asset: Asset): Entitlement | Asset | undefined {
    if (AssetUtils.isVod(asset)) {
      let entitlement: Entitlement | undefined;
      const entitlements = EntitlementsPpvSelectors.getPpvEntitlements(store().getState());
      entitlement = entitlements.find((e) => asset.streams.find((s) => s.id === e.fileId));
      if (entitlement !== undefined && TimeUtils.isAfterNow(entitlement.endDate)) {
        return entitlement;
      }

      const mediaFilesIds = asset.streams.map((stream) => stream.id);

      entitlement = BadgeEngine.getEntitlementCollectionByMediaFiles(mediaFilesIds);

      if (!entitlement) {
        entitlement = BadgeEngine.getEntitlementSubscriptionByMediaFiles(mediaFilesIds);
      }
      return entitlement;
    }
    return undefined;
  }

  protected static getAssetEntitledForFolder(asset: Asset): Entitlement | Asset | undefined {
    if (AssetUtils.isFolder(asset)) {
      let entitlements: (SubscriptionEntitlement | CollectionEntitlement)[] = [];

      if (asset.folderType === FolderType.SUBSCRIPTION) {
        entitlements = EntitlementsSubscriptionSelectors.getSubscriptionEntitlements(store().getState());
      } else if (asset.folderType === FolderType.BUNDLE) {
        entitlements = EntitlementsCollectionSelectors.getCollectionEntitlements(store().getState());
      }

      const entitlement = entitlements.find((e) => e.id === asset.productId);
      if (entitlement !== undefined && TimeUtils.isAfterNow(entitlement.endDate)) {
        return entitlement;
      }
    }
    return undefined;
  }

  static getAssetEntitled(asset: Asset): Entitlement | Asset | undefined {
    if (AssetUtils.isTvAsset(asset)) {
      const channel = PlayableChannelsSelectors.getChannelByEpgId(store().getState(), asset.channelEpgId);
      return channel;
    }

    const vodEntitled = BadgeEngine.getAssetEntitledForVod(asset);

    if (vodEntitled) {
      return vodEntitled;
    }

    const folderEntitled = BadgeEngine.getAssetEntitledForFolder(asset);

    if (folderEntitled) {
      return folderEntitled;
    }

    const entitledAssets = EntitlementsAssetsSelectors.getAssets(store().getState());
    return entitledAssets.find((e) => e.id === asset.id);
  }

  protected static getCurrencyIcon(): Icons {
    const currencyCode = Ducks.Currency.Selectors.getCurrencyCode(store().getState());
    switch (currencyCode) {
      case CurrencyCode.EUR:
        return Icons.EURO;
      case CurrencyCode.USD:
        return Icons.DOLLAR;
      case CurrencyCode.GBP:
      default:
        return Icons.POUND;
    }
  }

  protected static buildBadge(type: BadgeType, label?: string): Badge {
    switch (type) {
      case BadgeType.PLAY:
        return {
          type,
          icon: Icons.PLAY_CIRCLE,
          label,
        };
      case BadgeType.AIRS_ON:
        return {
          type,
          icon: Icons.AIRS_ON,
          label,
        };
      case BadgeType.EXPIRY_CLOCK:
        return {
          type,
          icon: Icons.CLOCK,
          label,
        };
      case BadgeType.WISHLIST_ON:
      case BadgeType.WISHLIST_OFF:
        return {
          type,
          icon: Icons.WISHLIST,
          label,
        };
      case BadgeType.LIVE_RECORDING:
        return {
          type,
          icon: Icons.RECORDING_SINGLE,
          color: Colors.brand,
          label,
        };
      case BadgeType.SINGLE_SCHEDULED_RECORDING:
        return {
          type,
          icon: Icons.RECORDING_SINGLE_SCHEDULED,
          label,
        };
      case BadgeType.LIVE_SERIES_RECORDING:
        return {
          type,
          icon: Icons.RECORDING_SERIES,
          color: Colors.brand,
          label,
        };
      case BadgeType.SERIES_SCHEDULED_RECORDING:
        return {
          type,
          icon: Icons.RECORDING_SERIES_SCHEDULED,
          label,
        };
      case BadgeType.PROTECTED_RECORDING:
        return {
          type,
          icon: Icons.PROTECTED_RECORDING,
          label,
        };
      default:
        throw new Error('Badge type not founded');
    }
  }

  protected static getSingleRecordingAndSeriesRecording(
    asset: Readonly<TvAsset>
  ): [Recording | undefined, SeriesRecording | undefined] {
    const singlesRecordings = SingleRecordingsSelectors.getRecordings(store().getState());
    const singleRecording = singlesRecordings.find((e) => e.internalId === asset.internalId);

    const seriesId = singleRecording?.seriesId || asset.seriesId;
    const seriesRecordings = SeriesRecordingsSelectors.getRecordings(store().getState());
    const seriesRecording = seriesRecordings.find((e) => e.seriesId === seriesId);

    return [singleRecording, seriesRecording];
  }

  protected static toTimeFormat(epoch: number): string {
    const date = new Date(epoch);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    const weekday = TimeUtils.getShortWeekDay(epoch);
    const day = TimeUtils.getDay(epoch);
    const month = TimeUtils.getShortMonth(epoch);

    return translate
      .formatString(translate['badge.timeFormat'], {
        hours,
        minutes,
        weekday,
        day,
        month,
      })
      .toString();
  }
}
